import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusBadge } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type ReportStatus = "pending" | "reviewed" | "dismissed";
type Filter = "pending" | "reviewed" | "dismissed" | "all";

type Report = {
  id: string;
  reason: string;
  details: string | null;
  status: ReportStatus;
  createdAt: string;
  reviewedAt: string | null;
  conversationId: string | null;
  reporter: { id: string; name: string };
  reported: { id: string; name: string };
};

type Counts = { pending: number; reviewed: number; dismissed: number; total: number };

const FILTERS: { key: Filter; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "reviewed", label: "Reviewed" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
];

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  harassment: "Harassment",
  inappropriate: "Inappropriate content",
  fake: "Fake profile",
  scam: "Scam",
  other: "Other",
};

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function apiError(json: { message?: string; error?: string }, fallback: string) {
  return json.message || json.error || fallback;
}

export function ReportsPage() {
  const [filter, setFilter] = React.useState<Filter>("pending");
  const queryClient = useQueryClient();

  const reports = useQuery({
    queryKey: ["admin-reports", filter],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/reports?status=${filter}&limit=100`, {
        headers: getAdminHeaders(),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiError(json, "Could not load reports"));
      return json as { reports: Report[]; counts: Counts };
    },
  });

  const decide = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ReportStatus }) => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/reports/${id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ status }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(apiError(json, "Could not update the report"));
    },
    onSuccess: () => {
      toast.success("Report updated");
      queryClient.invalidateQueries({ queryKey: ["admin-reports"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const counts = reports.data?.counts;
  const rows = reports.data?.reports ?? [];

  return (
    <div className="space-y-5">
      <PageHeader title="Reports" description="Reports made from chat conversations. Decide what happens to each one." />

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Pending", value: counts?.pending ?? 0, tone: "warning" },
          { label: "Reviewed", value: counts?.reviewed ?? 0, tone: "positive" },
          { label: "Dismissed", value: counts?.dismissed ?? 0, tone: "muted" },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{stat.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{reports.isLoading ? "…" : stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <Button key={item.key} size="sm" variant={filter === item.key ? "default" : "outline"} onClick={() => setFilter(item.key)}>
            {item.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {reports.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : reports.isError ? (
            <p className="p-6 text-sm text-destructive">{(reports.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No reports in this list.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reported</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">{report.reported.name || "—"}</TableCell>
                    <TableCell>{report.reporter.name || "—"}</TableCell>
                    <TableCell>{REASON_LABELS[report.reason] ?? report.reason}</TableCell>
                    <TableCell className="max-w-xs whitespace-normal text-sm text-muted-foreground">{report.details || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(report.createdAt)}</TableCell>
                    <TableCell>
                      <StatusBadge value={report.status} />
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      {report.status !== "reviewed" && (
                        <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: report.id, status: "reviewed" })}>
                          Mark reviewed
                        </Button>
                      )}
                      {report.status !== "dismissed" && (
                        <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ id: report.id, status: "dismissed" })}>
                          Dismiss
                        </Button>
                      )}
                      {report.status !== "pending" && (
                        <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ id: report.id, status: "pending" })}>
                          Reopen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
